import { useCallback, useState } from 'react';

import {
  landed,
  refused,
  type WriteOutcome,
} from '@backrs_ui/grow-ui/src/components/InstitutionWrite/writeOutcome';
import {
  isLandedUnreadable,
  useMintCodeMutation,
  useRevokeCodeMutation,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import {
  codeStrings,
  mintedMessage,
  revokedMessage,
  revokeStrings,
} from './institutionAdminWriteMessages';
import { landedProse, writeProse } from './institutionAdminWriteProse';

/**
 * The two code doors, shared by every surface that shows a line's code. Neither the mint nor the
 * revoke reads any page state, so a surface can mount this without the rest of a page's controller.
 */
export const useCodeDoors = () => {
  const [mintCode, mintCodeState] = useMintCodeMutation();
  const [revokeCode, revokeCodeState] = useRevokeCodeMutation();

  // The LINE each write was fired from. One mutation instance serves every line, so its
  // isLoading is global: without this, one line's write shows every line as pending.
  const [createCodeLine, setCreateCodeLine] = useState<string | null>(null);
  const [revokeLine, setRevokeLine] = useState<string | null>(null);

  const onCreateCode = useCallback(
    async (
      rosterId: number,
      cohortId: number | null,
      lineKey: string,
      code: string | null = null,
    ): Promise<WriteOutcome> => {
      setCreateCodeLine(lineKey);

      const outcome = await mintCode({ rosterId, cohortId, code })
        .unwrap()
        .then((minted) => ({ minted, error: null as unknown }))
        .catch((error: unknown) => {
          console.error('Code create failed', error);

          return { minted: null, error };
        });

      if (outcome.minted !== null) return landed(mintedMessage(outcome.minted));

      if (isLandedUnreadable(outcome.error))
        return landed(landedProse(outcome.error, codeStrings.mint.unreadable));

      return refused(writeProse(outcome.error));
    },
    [mintCode],
  );

  const onRevokeCode = useCallback(
    async (rosterId: number, codeId: number, lineKey: string): Promise<WriteOutcome> => {
      setRevokeLine(lineKey);

      const outcome = await revokeCode({ rosterId, codeId })
        .unwrap()
        .then((code) => ({ code, error: null as unknown }))
        .catch((error: unknown) => {
          console.error('Code revoke failed', error);

          return { code: null, error };
        });

      if (outcome.code !== null) return landed(revokedMessage(outcome.code));

      if (isLandedUnreadable(outcome.error))
        return landed(landedProse(outcome.error, revokeStrings.unreadable));

      return refused(writeProse(outcome.error));
    },
    [revokeCode],
  );

  return {
    /** The raw refusals from both code writes, undecoded. */
    errors: [mintCodeState.error, revokeCodeState.error],
    isWriteInFlight: mintCodeState.isLoading || revokeCodeState.isLoading,
    codeCreate: {
      isPendingFor: (lineKey: string) => mintCodeState.isLoading && createCodeLine === lineKey,
      isErrorFor: (lineKey: string) =>
        mintCodeState.isError &&
        createCodeLine === lineKey &&
        !isLandedUnreadable(mintCodeState.error),
      errorMessage: writeProse(mintCodeState.error),
      reset: mintCodeState.reset,
      onCreate: onCreateCode,
    },
    codeRevoke: {
      isPendingFor: (lineKey: string) => revokeCodeState.isLoading && revokeLine === lineKey,
      // Scoped on the SAME axis as the pending label. Unscoped, one line's refusal renders on
      // the next line's dialog, for a write that line never made.
      isErrorFor: (lineKey: string) =>
        revokeCodeState.isError &&
        revokeLine === lineKey &&
        !isLandedUnreadable(revokeCodeState.error),
      errorMessage: writeProse(revokeCodeState.error),
      /** A refusal belongs to the opening that produced it; the next one starts clean. */
      reset: revokeCodeState.reset,
      onRevoke: onRevokeCode,
    },
  };
};
